// Components/GalleryAdmin.js
import React, { useState, useEffect } from 'react';
import api, { uploadsBaseUrl } from '../api';
import './GalleryAdmin.css';
import DashboardLayout from './Dashboard/DashboardLayout';

const GalleryAdmin = () => {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    image: null,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    try {
      setLoading(true);
      const response = await api.get('/gallery-admin/all');
      setGalleries(response.data);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load galleries' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'Please select an image file' });
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!formData.title.trim()) {
      setMessage({ type: 'error', text: 'Title is required' });
      return;
    }

    if (!editingId && !selectedFile) {
      setMessage({ type: 'error', text: 'Please select an image for new gallery' });
      return;
    }

    try {
      setIsSubmitting(true);

      if (editingId) {
        // Update existing gallery
        await api.put(`/gallery/${editingId}`, {
          title: formData.title,
          description: formData.description,
          category: formData.category,
        });
        setMessage({ type: 'success', text: 'Gallery updated successfully!' });
        setEditingId(null);
      } else {
        // Create new gallery
        const formDataObj = new FormData();
        formDataObj.append('title', formData.title);
        formDataObj.append('description', formData.description);
        formDataObj.append('category', formData.category);
        formDataObj.append('image', selectedFile);

        await api.post('/gallery', formDataObj, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setMessage({ type: 'success', text: 'Gallery image uploaded successfully!' });
        setSelectedFile(null);
      }

      setFormData({ title: '', description: '', category: '', image: null });
      fetchGalleries();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save gallery' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = gallery => {
    setEditingId(gallery.id);
    setFormData({
      title: gallery.title,
      description: gallery.description || '',
      category: gallery.category || '',
      image: null,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({ title: '', description: '', category: '', image: null });
    setSelectedFile(null);
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await api.patch(`/gallery/${id}/status`, { status: newStatus });
      setMessage({ type: 'success', text: `Gallery status changed to ${newStatus}` });
      fetchGalleries();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update status' });
    }
  };

  const handleDelete = async id => {
    if (window.confirm('Are you sure you want to delete this gallery image?')) {
      try {
        await api.delete(`/gallery/${id}`);
        setMessage({ type: 'success', text: 'Gallery image deleted successfully!' });
        fetchGalleries();
      } catch (err) {
        setMessage({ type: 'error', text: 'Failed to delete gallery' });
      }
    }
  };

  const filteredGalleries =
    filterStatus === 'all' ? galleries : galleries.filter(g => g.status === filterStatus);

  if (loading) return <div className="admin-loading">Loading...</div>;

  return (
    <DashboardLayout>
      <div className="gallery-admin-container">
        <div className="admin-header">
          <h1>Gallery Management</h1>
          <p>Upload, edit, and manage gallery images</p>
        </div>

        {/* Message Alert */}
        {message && (
          <div className={`alert alert-${message.type}`}>
            <span>{message.text}</span>
            <button onClick={() => setMessage(null)}>✕</button>
          </div>
        )}

        {/* Upload Form */}
        <div className="admin-form-section">
          <h2>{editingId ? 'Edit Gallery' : 'Upload Gallery Image'}</h2>
          <form onSubmit={handleSubmit} className="gallery-form">
            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter image title"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter image description"
                rows="4"
              ></textarea>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="category">Category</label>
                <input
                  type="text"
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  placeholder="e.g., Projects, Events, Team"
                />
              </div>

              {!editingId && (
                <div className="form-group">
                  <label htmlFor="image">Image File *</label>
                  <input
                    type="file"
                    id="image"
                    onChange={handleFileChange}
                    accept="image/*"
                    required={!editingId}
                  />
                  {selectedFile && <p className="file-info">Selected: {selectedFile.name}</p>}
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="submit" disabled={isSubmitting} className="btn btn-primary">
                {isSubmitting ? 'Processing...' : editingId ? 'Update' : 'Upload'}
              </button>
              {editingId && (
                <button type="button" onClick={handleCancel} className="btn btn-secondary">
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Gallery List */}
        <div className="admin-list-section">
          <div className="list-header">
            <h2>Gallery Images ({filteredGalleries.length})</h2>
            <div className="status-filter">
              <button
                className={filterStatus === 'all' ? 'active' : ''}
                onClick={() => setFilterStatus('all')}
              >
                All
              </button>
              <button
                className={filterStatus === 'active' ? 'active' : ''}
                onClick={() => setFilterStatus('active')}
              >
                Active
              </button>
              <button
                className={filterStatus === 'inactive' ? 'active' : ''}
                onClick={() => setFilterStatus('inactive')}
              >
                Inactive
              </button>
            </div>
          </div>

          {filteredGalleries.length > 0 ? (
            <div className="gallery-table">
              <table>
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGalleries.map(gallery => (
                    <tr key={gallery.id}>
                      <td>
                        <img
                          src={`${uploadsBaseUrl}/${gallery.imageUrl}`}
                          alt={gallery.title}
                          className="table-image"
                        />
                      </td>
                      <td className="title-cell">
                        <strong>{gallery.title}</strong>
                        {gallery.description && <p>{gallery.description.substring(0, 50)}...</p>}
                      </td>
                      <td>{gallery.category || '-'}</td>
                      <td>
                        <select
                          value={gallery.status}
                          onChange={e => handleStatusChange(gallery.id, e.target.value)}
                          className={`status-select status-${gallery.status}`}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </td>
                      <td>{new Date(gallery.createdAt).toLocaleDateString()}</td>
                      <td className="actions-cell">
                        <button onClick={() => handleEdit(gallery)} className="btn btn-sm btn-edit">
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(gallery.id)}
                          className="btn btn-sm btn-delete"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">
              <p>No gallery images found</p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default GalleryAdmin;
